import { BREAKPOINTS } from '@/config/breakpoints';

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
  }
}

const SCRIPT_ID = 'crisp-chat-script';

/**
 * Вид виджета задаётся здесь, а не в панели Crisp: по умолчанию там синий
 * лаунчер, который спорит с дизайн-системой. Значения — из палитры Crisp Web SDK
 * (`config color:theme`: default, amber, black, blue, …, red, teal);
 * ближайшее к акценту `--accent` — `red`, нейтральное к обеим темам — `black`.
 */
const CHAT_COLOR = 'black';

function push(command: unknown[]): void {
  const crisp = window.$crisp;
  if (crisp && typeof crisp.push === 'function') crisp.push(command);
}

/**
 * Разрешён ли виджет рубильником админки (`GET /crisp-config`). Fail-safe:
 * включённым считается ровно булево true — строка, число, null и отсутствие
 * поля читаются как «выключено», и скрипт не монтируется.
 */
export function isCrispChatEnabled(payload: unknown): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  const crisp = (payload as { crisp?: unknown }).crisp;
  if (typeof crisp !== 'object' || crisp === null) return false;
  return (crisp as { enabled?: unknown }).enabled === true;
}

export function isCrispMounted(): boolean {
  return typeof document !== 'undefined' && !!document.getElementById(SCRIPT_ID);
}

/** Светлая или тёмная схема чата — по теме приложения (`data-theme` на `<html>`). */
export function applyCrispTheme(): void {
  const dark = document.documentElement.getAttribute('data-theme') !== 'light';
  push(['config', 'color:mode', [dark ? 'dark' : 'light']]);
}

/** Экраны, где лаунчер закрывает собой интерфейс (полотно канваса), прячут его. */
let hiddenByScreen = false;

/** Лаунчер: на десктопе виден всегда, на мобильном скрыт (NAV-7). */
export function applyCrispVisibility(): void {
  const isMobile = window.innerWidth <= BREAKPOINTS.mobile;
  push(['do', hiddenByScreen || isMobile ? 'chat:hide' : 'chat:show']);
}

/** Спрятать лаунчер на время экрана; возвращает состояние обратно вызовом с false. */
export function setCrispHiddenByScreen(hidden: boolean): void {
  hiddenByScreen = hidden;
  applyCrispVisibility();
}

export function mountCrisp(websiteId: string): void {
  if (!websiteId || isCrispMounted()) return;
  window.$crisp = window.$crisp ?? [];
  window.CRISP_WEBSITE_ID = websiteId;
  // Очередь наполняется до загрузки скрипта — Crisp разберёт её сам.
  push(['config', 'color:theme', [CHAT_COLOR]]);
  applyCrispTheme();
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.src = 'https://client.crisp.chat/l.js';
  s.async = true;
  document.head.appendChild(s);
  // После закрытия панели на мобильном лаунчер снова прячется.
  push(['on', 'chat:closed', applyCrispVisibility]);
}

/** Программное открытие чата (Помощь, запрос референса) — работает и на мобильном. */
export function openCrispChat(prefill?: string): boolean {
  if (!isCrispMounted()) return false;
  if (prefill) push(['set', 'message:text', [prefill]]);
  push(['do', 'chat:show']);
  push(['do', 'chat:open']);
  return true;
}
